import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, PlusCircle, CheckCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export default function PatternReportsCard() {
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(null);
  const [madeTopics, setMadeTopics] = useState({});

  const load = () => api.get("/reports/patterns").then((r) => setReports(r.data.reports)).catch(() => {});
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/reports/patterns/generate", {}, { timeout: 150000 });
      toast.success("Draft report ready — review it below");
      setOpen(data.report);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  const review = async (r) => {
    try {
      await api.post(`/reports/patterns/${r.id}/review`, {});
      toast.success("Marked reviewed");
      load();
      if (open?.id === r.id) setOpen({ ...open, status: "reviewed" });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const makeTraining = async (r, t) => {
    try {
      await api.post(`/reports/patterns/${r.id}/training`, { title: t.title, why: t.why });
      setMadeTopics((s) => ({ ...s, [`${r.id}:${t.title}`]: true }));
      toast.success("Draft training created — find it in Training Creator (inactive until you publish)");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="pattern-reports-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 font-heading text-lg font-semibold text-stone-900">
            <FileText size={18} className="text-primary" /> Recurring-scenario reports
          </p>
          <p className="mt-0.5 text-xs text-stone-500">Drafted from note patterns — what keeps happening, whether we're following our own policies, and the trainings that would help. Never sent anywhere automatically.</p>
        </div>
        <button onClick={generate} disabled={busy}
          className="rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-stone-900 disabled:opacity-50"
          data-testid="pattern-report-generate">
          {busy ? "Reading the notes…" : "Draft a report"}
        </button>
      </div>
      {reports.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {reports.map((r) => (
            <button key={r.id} onClick={async () => { const { data } = await api.get(`/reports/patterns/${r.id}`); setOpen(data); }}
              className="flex w-full flex-wrap items-center gap-2 rounded-lg border border-stone-100 bg-accent/20 px-4 py-2.5 text-left transition-colors hover:border-primary/30"
              data-testid={`pattern-report-row-${r.id}`}>
              <span className="text-sm font-medium text-stone-800">Report · {(r.created_at || "").slice(0, 10)}</span>
              <span className="text-xs text-stone-400">by {r.created_by}</span>
              <span className={`ml-auto rounded-full px-2.5 py-0.5 text-[10px] font-bold ${r.status === "reviewed" ? "bg-[#1e8449]/10 text-[#1e8449]" : "bg-[#B7791F]/10 text-[#8a5d14]"}`}
                data-testid={`pattern-report-status-${r.id}`}>
                {r.status === "reviewed" ? "Reviewed" : "DRAFT — needs review"}
              </span>
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="pattern-report-dialog">
          {open && (
            <>
              <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                Recurring-scenario report · {(open.created_at || "").slice(0, 10)}
                <button onClick={() => setOpen(null)} className="ml-auto text-stone-400 hover:text-stone-700" data-testid="pattern-report-close"><X size={18} /></button>
              </DialogTitle>
              </DialogHeader>
              <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4" data-testid="pattern-report-stats">
                {[["Notes (90d)", open.stats?.notes_90d], ["Overdue follow-ups", open.stats?.overdue_followups],
                  ["Open complaints", open.stats?.open_complaints], ["Complaints not logged", open.stats?.complaints_missing_log]].map(([l, v]) => (
                  <div key={l} className="rounded-lg border border-stone-100 bg-accent/30 p-2.5 text-center">
                    <p className="font-heading text-lg font-bold text-stone-900">{v ?? 0}</p>
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">{l}</p>
                  </div>
                ))}
              </div>
              <div className="prose prose-sm mt-4 max-w-none whitespace-pre-wrap text-sm leading-relaxed text-stone-700" data-testid="pattern-report-body">
                {open.report_markdown}
              </div>
              {(open.suggested_trainings || []).length > 0 && (
                <div className="mt-4 rounded-lg border border-stone-200 bg-accent/20 p-3" data-testid="pattern-report-trainings">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-stone-400">Suggested trainings from these scenarios — draft only, nothing publishes itself</p>
                  <div className="mt-2 space-y-2">
                    {open.suggested_trainings.map((t, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2 rounded-lg bg-white p-2.5" data-testid={`pattern-training-${i}`}>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-stone-800">{t.title}</p>
                          <p className="text-xs text-stone-500">{t.why}</p>
                        </div>
                        {madeTopics[`${open.id}:${t.title}`] ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1e8449]"><CheckCircle size={13} /> Draft created</span>
                        ) : (
                          <button onClick={() => makeTraining(open, t)}
                            className="inline-flex items-center gap-1 rounded-full border border-primary/40 px-3 py-1 text-[11px] font-semibold text-primary hover:bg-primary hover:text-white"
                            data-testid={`pattern-training-create-${i}`}>
                            <PlusCircle size={13} /> Create draft
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="mt-2 text-[11px] text-stone-400">Drafts land inactive in <Link to="/training" className="font-semibold text-primary hover:underline">Training</Link> — you write/review before anyone is assigned.</p>
                </div>
              )}
              <div className="mt-4 flex gap-2">
                {open.status !== "reviewed" && (
                  <button onClick={() => review(open)} className="rounded-full bg-primary px-4 py-2 text-xs font-semibold text-white hover:bg-stone-900" data-testid="pattern-report-review">
                    Mark reviewed
                  </button>
                )}
                <button onClick={() => setOpen(null)} className="rounded-full border border-stone-300 px-4 py-2 text-xs font-medium text-stone-500 hover:bg-stone-100" data-testid="pattern-report-dismiss">Close</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
